import React, { useEffect, useState, useCallback } from 'react';
import {
  Card,
  Table,
  Space,
  Button,
  Select,
  Typography,
  App,
  Tooltip,
  Tag,
  Image,
} from 'antd';
import {
  EyeOutlined,
  SearchOutlined,
  ClearOutlined,
  PictureOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { Avatar } from 'antd';
import { resolveMedia } from '../../utils/media';
import { listProgressCompetitions } from '../../services/evaluations';
import usePermissions from '../../hooks/usePermissions';

const { Title } = Typography;
const { useApp } = App;

const STATUS_COLORS = {
  'Sin Evaluar': 'grey',
  'En Curso': '#BA955E',
  'Terminada': 'green',
  'Critical': 'red',
};

const CompetitionProgressList = () => {
  const { message } = useApp();
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(false);

  const { can } = usePermissions();

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [sorter, setSorter] = useState({});

  // filtros aplicados
  const [status, setStatus] = useState(null);

  // filtros draft
  const [draftStatus, setDraftStatus] = useState(null);

  useEffect(() => {
      const loadCatalogs = async () => {
        try {
          const statusesData = [
            {id: 'Sin Evaluar', description: 'Sin Evaluar'},
            {id: 'En Curso', description: 'En Curso'},
            {id: 'Terminada', description: 'Terminada'}
          ];
          setStatuses(statusesData || []);
          
        } catch (error) {
          console.error('Error al cargar catálogos:', error);
          message.error('No se pudieron cargar los catálogos');
        }
      };
      loadCatalogs();
    }, [message]);

  const fetchData = useCallback(
    async (
      page,
      pageSize,
      srt,
      st
    ) => {
      setLoading(true);
      try {
        const params = {
          page: page || pagination.current,
          pageSize: pageSize || pagination.pageSize,
          sortField: srt?.field,
          sortOrder: srt?.order,
          ...(st && { status: st }),
        };

        const { items, total } = await listProgressCompetitions(params);

        setData(items || []);
        setPagination(prev => ({
          ...prev,
          current: page || prev.current,
          pageSize: pageSize || prev.pageSize,
          total: total || 0,
        }));
      } catch {
        message.error('No se pudieron cargar las competencias');
      } finally {
        setLoading(false);
      }
    },
    [pagination, message]
  );

  useEffect(() => {
    fetchData(1, pagination.pageSize, null, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilters = ({ status = null } = {}) => {
    setStatus(status);

    fetchData(
      1,
      pagination.pageSize,
      sorter,
      status
    );
  };

  const handleSearch = () => {
    applyFilters({ status: draftStatus });
  };

  const handleClearFilters = () => {
    setDraftStatus(null);
    applyFilters();
  };
  const handleViewJudges = (id) => {
    navigate(`/evaluations/competitions/${id}`)
  };

  const onTableChange = (pag, filters, srt) => {
    setSorter(srt || {});
    fetchData(
      pag.current,
      pag.pageSize,
      srt,
      status
    );
  };

  const renderActions = (record) => {
    return (
      can('competitions_eval.view') && (<Button
        type="primary"
        icon={<EyeOutlined />}
        onClick={() => handleViewJudges(record.id)}
      >
        Ver Jueces
      </Button>)
    );
  };

  const columns = [
    {
      title: 'Imagen',
      dataIndex: 'urlImage',
      key: 'image',
      width: 96,
      align: 'center',
      render: (_, r) => {
        const mediaPath = r.urlImage || r.media_path;
        const src = resolveMedia(mediaPath);
        const label = r.title || 'competencia';
        if (!src) {
          return <Avatar shape="square" size={64} icon={<PictureOutlined />} />;
        }
        return (
          <Image
            src={src}
            alt={label}
            width={64}
            height={64}
            style={{ objectFit: 'cover', borderRadius: 8 }}
            onClick={(e) => e.stopPropagation()}
            preview={{ mask: 'Ver' }}
            fallback="data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='64' height='64'/>"
          />
        );
      },
    },
    {
      title: 'Competencia',
      dataIndex: 'folio',
      sorter: true,
    },
    {
      title: 'Modalidad',
      dataIndex: 'modality',
      render: (_, r) => r.modality || '-',
    },
    {
      title: 'Fecha Inicio Evaluación',
      dataIndex: 'start_datetime',
      render: d => d ? moment(d).format('DD/MM/YYYY hh:mm A') : '-',
      sorter: true,
    },
    {
      title: 'Fecha Fin Evaluación',
      dataIndex: 'end_datetime',
      render: (d, r) => {
        if (!d) return '-';

        const isCritical = r.critical;

        return (
          <span style={{ color: isCritical ? 'red' : undefined, fontWeight: isCritical ? 600 : 400 }}>
            {moment(d).format('DD/MM/YYYY hh:mm A')}
          </span>
        );
      },
      sorter: true,
    },
    {
      title: 'Jueces',
      dataIndex: 'judges_finished',
      render: (_, r) => (r.judges_finished + ' de ' + r.total_judges) || '-',
      sorter: true,
    },
    {
      title: 'Estatus',
      dataIndex: 'computed_status',
      align: 'center',
      render: (status, r) => {
        const isCritical = r.critical;

        const displayStatus = isCritical ? 'Critical' : status;

        return (
          <Tag color={STATUS_COLORS[displayStatus] || 'default'}>
            {status}
          </Tag>
        );
      },
    },
    {
      title: 'Acciones',
      align: 'center',
      render: (_, r) => renderActions(r),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title level={3}>Competencias en Evaluación</Title>

      <Card>
        <Space wrap style={{ marginBottom: 16 }}>
          <Select
            placeholder="Estatus"
            allowClear
            style={{ width: 200 }}
            value={draftStatus}
            onChange={setDraftStatus}
            options={statuses.map((status) => ({
                    value: status.id,
                    label: `${status.description}`,
                  }))}
          />
          <Tooltip title="Buscar">
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={handleSearch}
            />
          </Tooltip>
          <Tooltip title="Limpiar filtros">
            <Button
              icon={<ClearOutlined />}
              onClick={handleClearFilters}
            />
          </Tooltip>
        </Space>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={data}
          loading={loading}
          scroll={{ x: 'max-content' }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50, 100],
            showTotal: t => `Total: ${t} competencias`,
          }}
          onChange={onTableChange}
        />
      </Card>
    </div>
  );
};

export default CompetitionProgressList;
